// analyticsElementContext.js - Pure helpers for deriving analytics context
// (button label, nearest game, current page) from a clicked DOM element.
//
// Used by AnalyticsContext's delegated click listener (generic "button_click"
// coverage) and by GameSearchDropdown (explicit "game_search_select"), so the
// same resolution rules apply everywhere a click is tracked.

const MAX_LABEL_LENGTH = 60;

/**
 * Elements considered "interactive" for the purposes of generic click
 * tracking. Clicking inside one of these (or an element explicitly opted in
 * via data-track-id) is treated as a trackable user action.
 */
const INTERACTIVE_SELECTOR =
  'button, a, [role="button"], input[type="submit"], input[type="button"], [data-track-id]';

/**
 * Derive a human-readable label for an element, preferring explicit,
 * developer-authored names over automatic fallbacks.
 *
 * Priority: data-track-label > aria-label > data-track-id > text content > tag/class fallback.
 */
export function getElementLabel(el) {
  if (!el) return 'unknown';

  const explicit =
    el.getAttribute?.('data-track-label') ||
    el.getAttribute?.('aria-label') ||
    el.getAttribute?.('data-track-id');
  if (explicit) return explicit.trim();

  const text = el.textContent?.trim().replace(/\s+/g, ' ');
  if (text) {
    return text.length > MAX_LABEL_LENGTH ? `${text.slice(0, MAX_LABEL_LENGTH)}…` : text;
  }

  const tag = el.tagName ? el.tagName.toLowerCase() : 'element';
  const firstClass = typeof el.className === 'string' ? el.className.split(' ')[0] : '';
  return firstClass ? `${tag}.${firstClass}` : tag;
}

/**
 * Find the nearest ancestor (inclusive) carrying game context via
 * data-game-id / data-game-name attributes. Returns {} if none found.
 */
export function getGameContext(el) {
  const gameEl = el?.closest?.('[data-game-id]');
  if (!gameEl) return {};

  const gameId = gameEl.getAttribute('data-game-id');
  const gameName = gameEl.getAttribute('data-game-name');

  const context = {};
  if (gameId) context.game_id = gameId;
  if (gameName) context.game_name = gameName;
  return context;
}

/**
 * Find the nearest "interactive" ancestor (inclusive) for a click target,
 * or null if the click did not land on/inside a trackable element.
 */
export function getInteractiveAncestor(el) {
  return el?.closest?.(INTERACTIVE_SELECTOR) || null;
}

/** True if this element (or an ancestor) opts out of tracking entirely. */
export function isTrackingIgnored(el) {
  return !!el?.closest?.('[data-analytics-ignore]');
}

/** True if this element (or an ancestor) already emits its own named event. */
export function isTrackingHandled(el) {
  return !!el?.closest?.('[data-track-handled]');
}

/**
 * Build the metadata object for a generic or explicit click-based analytics
 * event: current page, resolved button label, and nearest game context.
 * `explicitMetadata` (if provided) wins over auto-derived fields on conflict.
 */
export function buildClickContext(el, explicitMetadata = {}) {
  const page = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const label = el ? getElementLabel(el) : undefined;
  const gameContext = el ? getGameContext(el) : {};

  return {
    ...(page ? { page } : {}),
    ...(label ? { button: label } : {}),
    ...gameContext,
    ...explicitMetadata,
  };
}
